import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  MapPin, Layers, Box, Grid, Printer, Search, Plus, Edit, Trash2,
  TrendingUp, AlertTriangle, CheckCircle2, QrCode, Maximize2, Minimize2, Download
} from 'lucide-react';
import { WarehousePDFGenerator } from '../../utils/warehousePDFGenerator';

const SpatialLocationManagement = () => {
  const [selectedWarehouse, setSelectedWarehouse] = useState(1);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedAisle, setSelectedAisle] = useState(null);
  const [viewMode, setViewMode] = useState('hierarchy'); // hierarchy, visual, slotting
  const [expandedNodes, setExpandedNodes] = useState({ 'zone-A': true }); // Auto-expand first zone by default

  // Mock data for hierarchical warehouse structure - Comprehensive data
  const warehouseStructure = {
    1: { // Main Warehouse - Large facility
      name: 'Main Warehouse',
      zones: [
        {
          id: 'A',
          name: 'Zone A - Electronics',
          aisles: [
            {
              id: 'A1',
              name: 'Aisle A1',
              racks: [
                {
                  id: 'A1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'A1-R1-S1', name: 'Shelf 1', bins: ['A1-R1-S1-B1', 'A1-R1-S1-B2', 'A1-R1-S1-B3', 'A1-R1-S1-B4'] },
                    { id: 'A1-R1-S2', name: 'Shelf 2', bins: ['A1-R1-S2-B1', 'A1-R1-S2-B2', 'A1-R1-S2-B3'] },
                    { id: 'A1-R1-S3', name: 'Shelf 3', bins: ['A1-R1-S3-B1', 'A1-R1-S3-B2'] },
                    { id: 'A1-R1-S4', name: 'Shelf 4', bins: ['A1-R1-S4-B1'] },
                  ]
                },
                {
                  id: 'A1-R2',
                  name: 'Rack 2',
                  shelves: [
                    { id: 'A1-R2-S1', name: 'Shelf 1', bins: ['A1-R2-S1-B1', 'A1-R2-S1-B2', 'A1-R2-S1-B3'] },
                    { id: 'A1-R2-S2', name: 'Shelf 2', bins: ['A1-R2-S2-B1', 'A1-R2-S2-B2'] },
                    { id: 'A1-R2-S3', name: 'Shelf 3', bins: ['A1-R2-S3-B1'] },
                  ]
                },
                {
                  id: 'A1-R3',
                  name: 'Rack 3',
                  shelves: [
                    { id: 'A1-R3-S1', name: 'Shelf 1', bins: ['A1-R3-S1-B1', 'A1-R3-S1-B2'] },
                    { id: 'A1-R3-S2', name: 'Shelf 2', bins: ['A1-R3-S2-B1', 'A1-R3-S2-B2', 'A1-R3-S2-B3'] },
                  ]
                }
              ]
            },
            {
              id: 'A2',
              name: 'Aisle A2',
              racks: [
                {
                  id: 'A2-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'A2-R1-S1', name: 'Shelf 1', bins: ['A2-R1-S1-B1', 'A2-R1-S1-B2', 'A2-R1-S1-B3', 'A2-R1-S1-B4'] },
                    { id: 'A2-R1-S2', name: 'Shelf 2', bins: ['A2-R1-S2-B1', 'A2-R1-S2-B2'] },
                    { id: 'A2-R1-S3', name: 'Shelf 3', bins: ['A2-R1-S3-B1', 'A2-R1-S3-B2', 'A2-R1-S3-B3'] },
                  ]
                },
                {
                  id: 'A2-R2',
                  name: 'Rack 2',
                  shelves: [
                    { id: 'A2-R2-S1', name: 'Shelf 1', bins: ['A2-R2-S1-B1', 'A2-R2-S1-B2'] },
                    { id: 'A2-R2-S2', name: 'Shelf 2', bins: ['A2-R2-S2-B1', 'A2-R2-S2-B2', 'A2-R2-S2-B3', 'A2-R2-S2-B4'] },
                  ]
                }
              ]
            },
            {
              id: 'A3',
              name: 'Aisle A3',
              racks: [
                {
                  id: 'A3-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'A3-R1-S1', name: 'Shelf 1', bins: ['A3-R1-S1-B1', 'A3-R1-S1-B2'] },
                    { id: 'A3-R1-S2', name: 'Shelf 2', bins: ['A3-R1-S2-B1'] },
                  ]
                },
                {
                  id: 'A3-R2',
                  name: 'Rack 2',
                  shelves: [
                    { id: 'A3-R2-S1', name: 'Shelf 1', bins: ['A3-R2-S1-B1', 'A3-R2-S1-B2', 'A3-R2-S1-B3'] },
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'B',
          name: 'Zone B - Furniture',
          aisles: [
            {
              id: 'B1',
              name: 'Aisle B1',
              racks: [
                {
                  id: 'B1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'B1-R1-S1', name: 'Shelf 1', bins: ['B1-R1-S1-B1', 'B1-R1-S1-B2'] },
                    { id: 'B1-R1-S2', name: 'Shelf 2', bins: ['B1-R1-S2-B1', 'B1-R1-S2-B2'] },
                    { id: 'B1-R1-S3', name: 'Shelf 3', bins: ['B1-R1-S3-B1'] },
                  ]
                },
                {
                  id: 'B1-R2',
                  name: 'Rack 2',
                  shelves: [
                    { id: 'B1-R2-S1', name: 'Shelf 1', bins: ['B1-R2-S1-B1', 'B1-R2-S1-B2'] },
                    { id: 'B1-R2-S2', name: 'Shelf 2', bins: ['B1-R2-S2-B1'] },
                  ]
                }
              ]
            },
            {
              id: 'B2',
              name: 'Aisle B2',
              racks: [
                {
                  id: 'B2-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'B2-R1-S1', name: 'Shelf 1', bins: ['B2-R1-S1-B1', 'B2-R1-S1-B2', 'B2-R1-S1-B3'] },
                    { id: 'B2-R1-S2', name: 'Shelf 2', bins: ['B2-R1-S2-B1'] },
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'C',
          name: 'Zone C - Appliances',
          aisles: [
            {
              id: 'C1',
              name: 'Aisle C1',
              racks: [
                {
                  id: 'C1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'C1-R1-S1', name: 'Shelf 1', bins: ['C1-R1-S1-B1', 'C1-R1-S1-B2'] },
                    { id: 'C1-R1-S2', name: 'Shelf 2', bins: ['C1-R1-S2-B1', 'C1-R1-S2-B2', 'C1-R1-S2-B3'] },
                  ]
                },
                {
                  id: 'C1-R2',
                  name: 'Rack 2',
                  shelves: [
                    { id: 'C1-R2-S1', name: 'Shelf 1', bins: ['C1-R2-S1-B1'] },
                    { id: 'C1-R2-S2', name: 'Shelf 2', bins: ['C1-R2-S2-B1', 'C1-R2-S2-B2'] },
                  ]
                }
              ]
            },
            {
              id: 'C2',
              name: 'Aisle C2',
              racks: [
                {
                  id: 'C2-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'C2-R1-S1', name: 'Shelf 1', bins: ['C2-R1-S1-B1', 'C2-R1-S1-B2'] },
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'D',
          name: 'Zone D - Raw Materials',
          aisles: [
            {
              id: 'D1',
              name: 'Aisle D1',
              racks: [
                {
                  id: 'D1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'D1-R1-S1', name: 'Shelf 1', bins: ['D1-R1-S1-B1', 'D1-R1-S1-B2', 'D1-R1-S1-B3', 'D1-R1-S1-B4'] },
                    { id: 'D1-R1-S2', name: 'Shelf 2', bins: ['D1-R1-S2-B1', 'D1-R1-S2-B2'] },
                  ]
                },
                {
                  id: 'D1-R2',
                  name: 'Rack 2',
                  shelves: [
                    { id: 'D1-R2-S1', name: 'Shelf 1', bins: ['D1-R2-S1-B1', 'D1-R2-S1-B2', 'D1-R2-S1-B3'] },
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'E',
          name: 'Zone E - Office Supplies',
          aisles: [
            {
              id: 'E1',
              name: 'Aisle E1',
              racks: [
                {
                  id: 'E1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'E1-R1-S1', name: 'Shelf 1', bins: ['E1-R1-S1-B1', 'E1-R1-S1-B2'] },
                    { id: 'E1-R1-S2', name: 'Shelf 2', bins: ['E1-R1-S2-B1', 'E1-R1-S2-B2', 'E1-R1-S2-B3'] },
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    2: { // Distribution Center A
      name: 'Distribution Center A',
      zones: [
        {
          id: 'A',
          name: 'Zone A - Fast Moving',
          aisles: [
            {
              id: 'A1',
              name: 'Aisle A1',
              racks: [
                {
                  id: 'A1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'A1-R1-S1', name: 'Shelf 1', bins: ['A1-R1-S1-B1', 'A1-R1-S1-B2', 'A1-R1-S1-B3'] },
                    { id: 'A1-R1-S2', name: 'Shelf 2', bins: ['A1-R1-S2-B1', 'A1-R1-S2-B2'] },
                  ]
                },
                {
                  id: 'A1-R2',
                  name: 'Rack 2',
                  shelves: [
                    { id: 'A1-R2-S1', name: 'Shelf 1', bins: ['A1-R2-S1-B1'] },
                    { id: 'A1-R2-S2', name: 'Shelf 2', bins: ['A1-R2-S2-B1', 'A1-R2-S2-B2'] },
                  ]
                }
              ]
            },
            {
              id: 'A2',
              name: 'Aisle A2',
              racks: [
                {
                  id: 'A2-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'A2-R1-S1', name: 'Shelf 1', bins: ['A2-R1-S1-B1', 'A2-R1-S1-B2'] },
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'B',
          name: 'Zone B - Standard Items',
          aisles: [
            {
              id: 'B1',
              name: 'Aisle B1',
              racks: [
                {
                  id: 'B1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'B1-R1-S1', name: 'Shelf 1', bins: ['B1-R1-S1-B1', 'B1-R1-S1-B2', 'B1-R1-S1-B3'] },
                    { id: 'B1-R1-S2', name: 'Shelf 2', bins: ['B1-R1-S2-B1'] },
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    3: { // Cold Storage
      name: 'Cold Storage',
      zones: [
        {
          id: 'A',
          name: 'Zone A - Frozen Foods',
          aisles: [
            {
              id: 'A1',
              name: 'Aisle A1',
              racks: [
                {
                  id: 'A1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'A1-R1-S1', name: 'Shelf 1', bins: ['A1-R1-S1-B1', 'A1-R1-S1-B2'] },
                    { id: 'A1-R1-S2', name: 'Shelf 2', bins: ['A1-R1-S2-B1', 'A1-R1-S2-B2', 'A1-R1-S2-B3'] },
                  ]
                },
                {
                  id: 'A1-R2',
                  name: 'Rack 2',
                  shelves: [
                    { id: 'A1-R2-S1', name: 'Shelf 1', bins: ['A1-R2-S1-B1'] },
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'B',
          name: 'Zone B - Dairy',
          aisles: [
            {
              id: 'B1',
              name: 'Aisle B1',
              racks: [
                {
                  id: 'B1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'B1-R1-S1', name: 'Shelf 1', bins: ['B1-R1-S1-B1', 'B1-R1-S1-B2'] },
                    { id: 'B1-R1-S2', name: 'Shelf 2', bins: ['B1-R1-S2-B1'] },
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'C',
          name: 'Zone C - Beverages',
          aisles: [
            {
              id: 'C1',
              name: 'Aisle C1',
              racks: [
                {
                  id: 'C1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'C1-R1-S1', name: 'Shelf 1', bins: ['C1-R1-S1-B1', 'C1-R1-S1-B2', 'C1-R1-S1-B3'] },
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    4: { // Express Hub
      name: 'Express Hub',
      zones: [
        {
          id: 'A',
          name: 'Zone A - Express Orders',
          aisles: [
            {
              id: 'A1',
              name: 'Aisle A1',
              racks: [
                {
                  id: 'A1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'A1-R1-S1', name: 'Shelf 1', bins: ['A1-R1-S1-B1', 'A1-R1-S1-B2', 'A1-R1-S1-B3', 'A1-R1-S1-B4'] },
                    { id: 'A1-R1-S2', name: 'Shelf 2', bins: ['A1-R1-S2-B1', 'A1-R1-S2-B2'] },
                  ]
                },
                {
                  id: 'A1-R2',
                  name: 'Rack 2',
                  shelves: [
                    { id: 'A1-R2-S1', name: 'Shelf 1', bins: ['A1-R2-S1-B1', 'A1-R2-S1-B2'] },
                  ]
                }
              ]
            },
            {
              id: 'A2',
              name: 'Aisle A2',
              racks: [
                {
                  id: 'A2-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'A2-R1-S1', name: 'Shelf 1', bins: ['A2-R1-S1-B1', 'A2-R1-S1-B2', 'A2-R1-S1-B3'] },
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'B',
          name: 'Zone B - Priority Orders',
          aisles: [
            {
              id: 'B1',
              name: 'Aisle B1',
              racks: [
                {
                  id: 'B1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'B1-R1-S1', name: 'Shelf 1', bins: ['B1-R1-S1-B1', 'B1-R1-S1-B2'] },
                    { id: 'B1-R1-S2', name: 'Shelf 2', bins: ['B1-R1-S2-B1'] },
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'C',
          name: 'Zone C - Standard Orders',
          aisles: [
            {
              id: 'C1',
              name: 'Aisle C1',
              racks: [
                {
                  id: 'C1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'C1-R1-S1', name: 'Shelf 1', bins: ['C1-R1-S1-B1', 'C1-R1-S1-B2'] },
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'D',
          name: 'Zone D - Returns',
          aisles: [
            {
              id: 'D1',
              name: 'Aisle D1',
              racks: [
                {
                  id: 'D1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'D1-R1-S1', name: 'Shelf 1', bins: ['D1-R1-S1-B1', 'D1-R1-S1-B2'] },
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'E',
          name: 'Zone E - Packaging',
          aisles: [
            {
              id: 'E1',
              name: 'Aisle E1',
              racks: [
                {
                  id: 'E1-R1',
                  name: 'Rack 1',
                  shelves: [
                    { id: 'E1-R1-S1', name: 'Shelf 1', bins: ['E1-R1-S1-B1'] },
                  ]
                }
              ]
            }
          ]
        }
      ]
    }
  };

  // Comprehensive bin data with capacity and utilization for all warehouses
  // Keys are prefixed with warehouse ID to ensure uniqueness
  const binData = {
    // Warehouse 1 - Main Warehouse - Zone A (Electronics)
    '1-A1-R1-S1-B1': { capacity: 100, used: 75, item: 'Laptop Dell XPS 15', velocity: 'high', status: 'occupied' },
    '1-A1-R1-S1-B2': { capacity: 100, used: 45, item: 'Monitor 27" 4K', velocity: 'medium', status: 'partial' },
    '1-A1-R1-S1-B3': { capacity: 100, used: 88, item: 'Gaming Mouse RGB', velocity: 'high', status: 'occupied' },
    '1-A1-R1-S1-B4': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-A1-R1-S2-B1': { capacity: 100, used: 100, item: 'Keyboard Mechanical', velocity: 'high', status: 'full' },
    '1-A1-R1-S2-B2': { capacity: 100, used: 30, item: 'Mouse Wireless', velocity: 'low', status: 'partial' },
    '1-A1-R1-S2-B3': { capacity: 100, used: 65, item: 'Webcam HD', velocity: 'medium', status: 'partial' },
    '1-A1-R1-S3-B1': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-A1-R1-S3-B2': { capacity: 100, used: 42, item: 'USB Hub', velocity: 'medium', status: 'partial' },
    '1-A1-R1-S4-B1': { capacity: 100, used: 95, item: 'External SSD 1TB', velocity: 'high', status: 'occupied' },
    '1-A1-R2-S1-B1': { capacity: 100, used: 60, item: 'Tablet iPad Pro', velocity: 'medium', status: 'partial' },
    '1-A1-R2-S1-B2': { capacity: 100, used: 78, item: 'Apple Pencil', velocity: 'high', status: 'occupied' },
    '1-A1-R2-S1-B3': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-A1-R2-S2-B1': { capacity: 100, used: 90, item: 'Phone iPhone 15', velocity: 'high', status: 'occupied' },
    '1-A1-R2-S3-B1': { capacity: 100, used: 35, item: 'Phone Case', velocity: 'medium', status: 'partial' },
    '1-A1-R3-S1-B1': { capacity: 100, used: 55, item: 'Smart Watch', velocity: 'medium', status: 'partial' },
    '1-A1-R3-S1-B2': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-A1-R3-S2-B1': { capacity: 100, used: 82, item: 'Wireless Earbuds', velocity: 'high', status: 'occupied' },
    '1-A1-R3-S2-B2': { capacity: 100, used: 40, item: 'Charging Cable', velocity: 'medium', status: 'partial' },
    '1-A1-R3-S2-B3': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-A2-R1-S1-B1': { capacity: 100, used: 50, item: 'Headphones Wireless', velocity: 'medium', status: 'partial' },
    '1-A2-R1-S1-B2': { capacity: 100, used: 80, item: 'Speakers Bluetooth', velocity: 'low', status: 'occupied' },
    '1-A2-R1-S1-B3': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-A2-R1-S1-B4': { capacity: 100, used: 92, item: 'Soundbar', velocity: 'medium', status: 'occupied' },
    '1-A2-R1-S2-B1': { capacity: 100, used: 100, item: 'Camera DSLR', velocity: 'medium', status: 'full' },
    '1-A2-R1-S2-B2': { capacity: 100, used: 25, item: 'Tripod', velocity: 'low', status: 'partial' },
    '1-A2-R1-S3-B1': { capacity: 100, used: 68, item: 'Camera Lens', velocity: 'low', status: 'partial' },
    '1-A2-R1-S3-B2': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-A2-R1-S3-B3': { capacity: 100, used: 45, item: 'Memory Card 128GB', velocity: 'medium', status: 'partial' },
    '1-A2-R2-S1-B1': { capacity: 100, used: 70, item: 'Action Camera', velocity: 'medium', status: 'partial' },
    '1-A2-R2-S1-B2': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-A2-R2-S2-B1': { capacity: 100, used: 85, item: 'Drone', velocity: 'low', status: 'occupied' },
    '1-A2-R2-S2-B2': { capacity: 100, used: 38, item: 'Drone Battery', velocity: 'low', status: 'partial' },
    '1-A2-R2-S2-B3': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-A2-R2-S2-B4': { capacity: 100, used: 62, item: 'Gimbal Stabilizer', velocity: 'low', status: 'partial' },
    '1-A3-R1-S1-B1': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-A3-R1-S1-B2': { capacity: 100, used: 55, item: 'VR Headset', velocity: 'low', status: 'partial' },
    '1-A3-R1-S2-B1': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-A3-R2-S1-B1': { capacity: 100, used: 48, item: 'Gaming Controller', velocity: 'medium', status: 'partial' },
    '1-A3-R2-S1-B2': { capacity: 100, used: 73, item: 'Gaming Keyboard', velocity: 'high', status: 'occupied' },
    '1-A3-R2-S1-B3': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    
    // Warehouse 1 - Zone B (Furniture)
    '1-B1-R1-S1-B1': { capacity: 200, used: 150, item: 'Office Chair Ergonomic', velocity: 'medium', status: 'occupied' },
    '1-B1-R1-S1-B2': { capacity: 200, used: 80, item: 'Desk Lamp LED', velocity: 'low', status: 'partial' },
    '1-B1-R1-S2-B1': { capacity: 200, used: 0, item: null, velocity: null, status: 'empty' },
    '1-B1-R1-S2-B2': { capacity: 200, used: 120, item: 'Standing Desk', velocity: 'medium', status: 'partial' },
    '1-B1-R1-S3-B1': { capacity: 200, used: 95, item: 'Monitor Stand', velocity: 'medium', status: 'partial' },
    '1-B1-R2-S1-B1': { capacity: 200, used: 160, item: 'Filing Cabinet', velocity: 'low', status: 'occupied' },
    '1-B1-R2-S1-B2': { capacity: 200, used: 0, item: null, velocity: null, status: 'empty' },
    '1-B1-R2-S2-B1': { capacity: 200, used: 70, item: 'Bookshelf', velocity: 'low', status: 'partial' },
    '1-B2-R1-S1-B1': { capacity: 200, used: 140, item: 'Conference Table', velocity: 'low', status: 'occupied' },
    '1-B2-R1-S1-B2': { capacity: 200, used: 0, item: null, velocity: null, status: 'empty' },
    '1-B2-R1-S1-B3': { capacity: 200, used: 110, item: 'Office Sofa', velocity: 'low', status: 'partial' },
    '1-B2-R1-S2-B1': { capacity: 200, used: 0, item: null, velocity: null, status: 'empty' },
    
    // Warehouse 1 - Zone C (Appliances)
    '1-C1-R1-S1-B1': { capacity: 150, used: 120, item: 'Air Conditioner 2 Ton', velocity: 'medium', status: 'occupied' },
    '1-C1-R1-S1-B2': { capacity: 150, used: 0, item: null, velocity: null, status: 'empty' },
    '1-C1-R1-S2-B1': { capacity: 150, used: 95, item: 'Refrigerator', velocity: 'medium', status: 'partial' },
    '1-C1-R1-S2-B2': { capacity: 150, used: 65, item: 'Washing Machine', velocity: 'medium', status: 'partial' },
    '1-C1-R1-S2-B3': { capacity: 150, used: 0, item: null, velocity: null, status: 'empty' },
    '1-C1-R2-S1-B1': { capacity: 150, used: 85, item: 'Microwave Oven', velocity: 'high', status: 'partial' },
    '1-C1-R2-S2-B1': { capacity: 150, used: 0, item: null, velocity: null, status: 'empty' },
    '1-C1-R2-S2-B2': { capacity: 150, used: 110, item: 'Dishwasher', velocity: 'low', status: 'occupied' },
    '1-C2-R1-S1-B1': { capacity: 150, used: 75, item: 'Coffee Maker', velocity: 'high', status: 'partial' },
    '1-C2-R1-S1-B2': { capacity: 150, used: 0, item: null, velocity: null, status: 'empty' },
    
    // Warehouse 1 - Zone D (Raw Materials)
    '1-D1-R1-S1-B1': { capacity: 500, used: 380, item: 'Steel Sheets 10mm', velocity: 'medium', status: 'occupied' },
    '1-D1-R1-S1-B2': { capacity: 500, used: 250, item: 'Aluminum Bars', velocity: 'medium', status: 'partial' },
    '1-D1-R1-S1-B3': { capacity: 500, used: 0, item: null, velocity: null, status: 'empty' },
    '1-D1-R1-S1-B4': { capacity: 500, used: 420, item: 'Copper Wire', velocity: 'high', status: 'occupied' },
    '1-D1-R1-S2-B1': { capacity: 500, used: 180, item: 'Plastic Pellets', velocity: 'medium', status: 'partial' },
    '1-D1-R1-S2-B2': { capacity: 500, used: 0, item: null, velocity: null, status: 'empty' },
    '1-D1-R2-S1-B1': { capacity: 500, used: 320, item: 'Wood Planks', velocity: 'low', status: 'occupied' },
    '1-D1-R2-S1-B2': { capacity: 500, used: 0, item: null, velocity: null, status: 'empty' },
    '1-D1-R2-S1-B3': { capacity: 500, used: 290, item: 'Concrete Mix', velocity: 'low', status: 'occupied' },
    
    // Warehouse 1 - Zone E (Office Supplies)
    '1-E1-R1-S1-B1': { capacity: 100, used: 75, item: 'Printer HP LaserJet', velocity: 'high', status: 'occupied' },
    '1-E1-R1-S1-B2': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    '1-E1-R1-S2-B1': { capacity: 100, used: 60, item: 'Paper A4 Ream', velocity: 'high', status: 'partial' },
    '1-E1-R1-S2-B2': { capacity: 100, used: 85, item: 'Ink Cartridges', velocity: 'high', status: 'occupied' },
    '1-E1-R1-S2-B3': { capacity: 100, used: 0, item: null, velocity: null, status: 'empty' },
    
    // Warehouse 2 - Distribution Center A
    '2-A1-R1-S1-B1': { capacity: 120, used: 95, item: 'Fast Moving Item A', velocity: 'high', status: 'occupied' },
    '2-A1-R1-S1-B2': { capacity: 120, used: 65, item: 'Fast Moving Item B', velocity: 'high', status: 'partial' },
    '2-A1-R1-S1-B3': { capacity: 120, used: 0, item: null, velocity: null, status: 'empty' },
    '2-A1-R1-S2-B1': { capacity: 120, used: 110, item: 'Fast Moving Item C', velocity: 'high', status: 'occupied' },
    '2-A1-R1-S2-B2': { capacity: 120, used: 0, item: null, velocity: null, status: 'empty' },
    '2-A1-R2-S1-B1': { capacity: 120, used: 80, item: 'Fast Moving Item D', velocity: 'high', status: 'partial' },
    '2-A1-R2-S2-B1': { capacity: 120, used: 0, item: null, velocity: null, status: 'empty' },
    '2-A1-R2-S2-B2': { capacity: 120, used: 55, item: 'Fast Moving Item E', velocity: 'high', status: 'partial' },
    '2-A2-R1-S1-B1': { capacity: 120, used: 0, item: null, velocity: null, status: 'empty' },
    '2-A2-R1-S1-B2': { capacity: 120, used: 70, item: 'Standard Item A', velocity: 'medium', status: 'partial' },
    '2-B1-R1-S1-B1': { capacity: 120, used: 45, item: 'Standard Item B', velocity: 'medium', status: 'partial' },
    '2-B1-R1-S1-B2': { capacity: 120, used: 0, item: null, velocity: null, status: 'empty' },
    '2-B1-R1-S1-B3': { capacity: 120, used: 88, item: 'Standard Item C', velocity: 'medium', status: 'occupied' },
    '2-B1-R1-S2-B1': { capacity: 120, used: 0, item: null, velocity: null, status: 'empty' },
    
    // Warehouse 3 - Cold Storage
    '3-A1-R1-S1-B1': { capacity: 80, used: 65, item: 'Frozen Pizza', velocity: 'high', status: 'occupied' },
    '3-A1-R1-S1-B2': { capacity: 80, used: 0, item: null, velocity: null, status: 'empty' },
    '3-A1-R1-S2-B1': { capacity: 80, used: 72, item: 'Ice Cream', velocity: 'high', status: 'occupied' },
    '3-A1-R1-S2-B2': { capacity: 80, used: 45, item: 'Frozen Vegetables', velocity: 'medium', status: 'partial' },
    '3-A1-R1-S2-B3': { capacity: 80, used: 0, item: null, velocity: null, status: 'empty' },
    '3-A1-R2-S1-B1': { capacity: 80, used: 58, item: 'Frozen Meat', velocity: 'medium', status: 'partial' },
    '3-B1-R1-S1-B1': { capacity: 80, used: 68, item: 'Milk Cartons', velocity: 'high', status: 'occupied' },
    '3-B1-R1-S1-B2': { capacity: 80, used: 0, item: null, velocity: null, status: 'empty' },
    '3-B1-R1-S2-B1': { capacity: 80, used: 55, item: 'Cheese Blocks', velocity: 'medium', status: 'partial' },
    '3-C1-R1-S1-B1': { capacity: 80, used: 75, item: 'Soft Drinks', velocity: 'high', status: 'occupied' },
    '3-C1-R1-S1-B2': { capacity: 80, used: 0, item: null, velocity: null, status: 'empty' },
    '3-C1-R1-S1-B3': { capacity: 80, used: 62, item: 'Energy Drinks', velocity: 'high', status: 'partial' },
    
    // Warehouse 4 - Express Hub
    '4-A1-R1-S1-B1': { capacity: 50, used: 45, item: 'Express Order #001', velocity: 'high', status: 'occupied' },
    '4-A1-R1-S1-B2': { capacity: 50, used: 38, item: 'Express Order #002', velocity: 'high', status: 'occupied' },
    '4-A1-R1-S1-B3': { capacity: 50, used: 0, item: null, velocity: null, status: 'empty' },
    '4-A1-R1-S1-B4': { capacity: 50, used: 42, item: 'Express Order #003', velocity: 'high', status: 'occupied' },
    '4-A1-R1-S2-B1': { capacity: 50, used: 0, item: null, velocity: null, status: 'empty' },
    '4-A1-R1-S2-B2': { capacity: 50, used: 48, item: 'Express Order #004', velocity: 'high', status: 'occupied' },
    '4-A1-R2-S1-B1': { capacity: 50, used: 35, item: 'Express Order #005', velocity: 'high', status: 'occupied' },
    '4-A1-R2-S1-B2': { capacity: 50, used: 0, item: null, velocity: null, status: 'empty' },
    '4-A2-R1-S1-B1': { capacity: 50, used: 40, item: 'Express Order #006', velocity: 'high', status: 'occupied' },
    '4-A2-R1-S1-B2': { capacity: 50, used: 0, item: null, velocity: null, status: 'empty' },
    '4-A2-R1-S1-B3': { capacity: 50, used: 32, item: 'Express Order #007', velocity: 'high', status: 'occupied' },
    '4-B1-R1-S1-B1': { capacity: 50, used: 28, item: 'Priority Order #001', velocity: 'high', status: 'occupied' },
    '4-B1-R1-S1-B2': { capacity: 50, used: 0, item: null, velocity: null, status: 'empty' },
    '4-B1-R1-S2-B1': { capacity: 50, used: 44, item: 'Priority Order #002', velocity: 'high', status: 'occupied' },
    '4-C1-R1-S1-B1': { capacity: 50, used: 0, item: null, velocity: null, status: 'empty' },
    '4-C1-R1-S1-B2': { capacity: 50, used: 36, item: 'Standard Order #001', velocity: 'medium', status: 'occupied' },
    '4-D1-R1-S1-B1': { capacity: 50, used: 25, item: 'Return Item #001', velocity: 'low', status: 'partial' },
    '4-D1-R1-S1-B2': { capacity: 50, used: 0, item: null, velocity: null, status: 'empty' },
    '4-E1-R1-S1-B1': { capacity: 50, used: 30, item: 'Packaging Material', velocity: 'high', status: 'occupied' },
  };

  const toggleNode = (path) => {
    setExpandedNodes(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const getBinStatusColor = (status) => {
    switch (status) {
      case 'full': return 'bg-red-500';
      case 'occupied': return 'bg-orange-500';
      case 'partial': return 'bg-yellow-500';
      case 'empty': return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  const getVelocityColor = (velocity) => {
    switch (velocity) {
      case 'high': return 'text-green-600 bg-green-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-400 bg-gray-50';
    }
  };

  const generateBinLabel = (binId) => {
    return {
      binId,
      barcode: `WH-${binId}`,
      qrCode: `https://wms.lyncs.com/bin/${binId}`,
      location: binId.split('-').join(' → ')
    };
  };

  const handlePrintBinLabels = async () => {
    try {
      const current = warehouseStructure[selectedWarehouse];
      if (!current) {
        alert('Please select a warehouse');
        return;
      }

      // Collect all bins from the warehouse
      const allBins = [];
      current.zones.forEach(zone => {
        zone.aisles.forEach(aisle => {
          aisle.racks.forEach(rack => {
            rack.shelves.forEach(shelf => {
              shelf.bins.forEach(binId => {
                const fullBinId = `${selectedWarehouse}-${binId}`;
                const bin = binData[fullBinId] || { capacity: 100, used: 0, status: 'empty' };
                allBins.push({
                  id: binId,
                  location: `${zone.name} → ${aisle.name} → ${rack.name} → ${shelf.name}`,
                  item: bin.item || 'Empty',
                  capacity: bin.capacity,
                  used: bin.used,
                  status: bin.status
                });
              });
            });
          });
        });
      });

      const reportData = {
        reportId: `LOC-${Date.now()}`,
        warehouse: current.name,
        zones: current.zones.map(zone => ({
          name: zone.name,
          aisles: zone.aisles.length,
          racks: zone.aisles.reduce((sum, aisle) => sum + aisle.racks.length, 0),
          shelves: zone.aisles.reduce((sum, aisle) => 
            sum + aisle.racks.reduce((rackSum, rack) => rackSum + rack.shelves.length, 0), 0),
          bins: zone.aisles.reduce((sum, aisle) => 
            sum + aisle.racks.reduce((rackSum, rack) => 
              rackSum + rack.shelves.reduce((shelfSum, shelf) => shelfSum + shelf.bins.length, 0), 0), 0),
          utilization: 75 // Mock value
        })),
        bins: allBins.slice(0, 100) // Limit to first 100 bins for PDF
      };

      const blob = await WarehousePDFGenerator.generateLocationReport(reportData);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `warehouse-location-report-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert(`Location report generated successfully! ${allBins.length} bins included.`);
    } catch (error) {
      console.error('Error generating location report:', error);
      alert('Error generating report. Please try again.');
    }
  };

  const handleRunOptimization = () => {
    alert('Slotting optimization is running... This feature will analyze item velocity and suggest optimal locations.');
    // In a real implementation, this would call an API or run optimization logic
  };

  const getSlottingSuggestions = () => {
    // Mock slotting optimization logic
    return [
      {
        item: 'New Product XYZ',
        currentLocation: null,
        suggestedLocation: 'A1-R1-S1-B3',
        reason: 'High velocity item - place near shipping area',
        priority: 'high'
      },
      {
        item: 'Slow Moving Item ABC',
        currentLocation: 'A1-R1-S1-B1',
        suggestedLocation: 'A2-R1-S1-B4',
        reason: 'Low velocity - move to storage area',
        priority: 'medium'
      }
    ];
  };

  const renderHierarchy = () => {
    const current = warehouseStructure[selectedWarehouse];
    if (!current) {
      return (
        <div className="text-center py-8 text-gray-500">
          No warehouse structure found for selected warehouse
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {current.zones.map((zone) => {
          const zonePath = `zone-${zone.id}`;
          const isZoneExpanded = expandedNodes[zonePath];
          
          return (
            <div key={zone.id} className="border border-gray-200 rounded-lg">
              <div
                className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 cursor-pointer hover:from-orange-100 hover:to-amber-100 transition-colors"
                onClick={() => toggleNode(zonePath)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Layers className="w-5 h-5 text-orange-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">{zone.name}</h3>
                      <p className="text-sm text-gray-600">{zone.aisles.length} aisles</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {isZoneExpanded ? 'Collapse' : 'Expand'}
                    </span>
                    {isZoneExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </div>
                </div>
              </div>
              
              {isZoneExpanded && (
                <div className="p-4 bg-white space-y-3">
                  {zone.aisles.map((aisle) => {
                    const aislePath = `${zonePath}-aisle-${aisle.id}`;
                    const isAisleExpanded = expandedNodes[aislePath];
                    
                    return (
                      <div key={aisle.id} className="border border-gray-200 rounded-lg">
                        <div
                          className="p-3 bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors"
                          onClick={() => toggleNode(aislePath)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Grid className="w-4 h-4 text-blue-600" />
                              <span className="font-medium text-gray-900">{aisle.name}</span>
                              <span className="text-sm text-gray-600">({aisle.racks.length} racks)</span>
                            </div>
                            {isAisleExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                          </div>
                        </div>
                        
                        {isAisleExpanded && (
                          <div className="p-3 bg-white space-y-2">
                            {aisle.racks.map((rack) => {
                              const rackPath = `${aislePath}-rack-${rack.id}`;
                              const isRackExpanded = expandedNodes[rackPath];
                              
                              return (
                                <div key={rack.id} className="border border-gray-200 rounded-lg">
                                  <div
                                    className="p-2 bg-purple-50 cursor-pointer hover:bg-purple-100 transition-colors"
                                    onClick={() => toggleNode(rackPath)}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Box className="w-4 h-4 text-purple-600" />
                                        <span className="font-medium text-sm text-gray-900">{rack.name}</span>
                                        <span className="text-xs text-gray-600">({rack.shelves.length} shelves)</span>
                                      </div>
                                      {isRackExpanded ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                                    </div>
                                  </div>
                                  
                                  {isRackExpanded && (
                                    <div className="p-2 bg-white space-y-2">
                                      {rack.shelves.map((shelf) => {
                                        const shelfPath = `${rackPath}-shelf-${shelf.id}`;
                                        const isShelfExpanded = expandedNodes[shelfPath];
                                        
                                        return (
                                          <div key={shelf.id} className="border border-gray-200 rounded">
                                            <div
                                              className="p-2 bg-green-50 cursor-pointer hover:bg-green-100 transition-colors"
                                              onClick={() => toggleNode(shelfPath)}
                                            >
                                              <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium text-gray-900">{shelf.name}</span>
                                                <div className="flex items-center gap-2">
                                                  <span className="text-xs text-gray-600">{shelf.bins.length} bins</span>
                                                  {isShelfExpanded ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                                                </div>
                                              </div>
                                            </div>
                                            
                                            {isShelfExpanded && (
                                              <div className="p-2 bg-white grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                                {shelf.bins.map((binId) => {
                                                  const fullBinId = `${selectedWarehouse}-${binId}`;
                                                  const bin = binData[fullBinId] || { capacity: 100, used: 0, status: 'empty' };
                                                  const utilization = bin.capacity > 0 ? (bin.used / bin.capacity) * 100 : 0;
                                                  
                                                  return (
                                                    <div
                                                      key={binId}
                                                      className="p-2 border border-gray-200 rounded hover:shadow-md transition-shadow cursor-pointer"
                                                      onClick={() => {
                                                        const label = generateBinLabel(binId);
                                                        alert(`Bin Label:\n${label.binId}\nBarcode: ${label.barcode}\nLocation: ${label.location}`);
                                                      }}
                                                    >
                                                      <div className="flex items-center justify-between mb-1">
                                                        <span className="text-xs font-semibold text-gray-900">{binId.split('-').pop()}</span>
                                                        <div className={`w-2 h-2 rounded-full ${getBinStatusColor(bin.status)}`} />
                                                      </div>
                                                      {bin.item && (
                                                        <div className="text-xs text-gray-600 truncate mb-1">{bin.item}</div>
                                                      )}
                                                      <div className="flex items-center justify-between text-xs">
                                                        <span className="text-gray-500">{bin.used}/{bin.capacity}</span>
                                                        <span className={`px-1 py-0.5 rounded text-xs ${getVelocityColor(bin.velocity)}`}>
                                                          {bin.velocity || 'N/A'}
                                                        </span>
                                                      </div>
                                                      <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                                                        <div
                                                          className={`h-full ${getBinStatusColor(bin.status)}`}
                                                          style={{ width: `${utilization}%` }}
                                                        />
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderVisualMap = () => {
    const current = warehouseStructure[selectedWarehouse];
    if (!current) return null;

    return (
      <div className="space-y-6">
        {current.zones.map((zone) => (
          <div key={zone.id} className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{zone.name}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {zone.aisles.map((aisle) => (
                <div key={aisle.id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">{aisle.name}</h4>
                  <div className="space-y-2">
                    {aisle.racks.map((rack) => (
                      <div key={rack.id} className="bg-gray-50 rounded p-2">
                        <div className="text-sm font-medium text-gray-700 mb-2">{rack.name}</div>
                        <div className="grid grid-cols-4 gap-1">
                          {rack.shelves.map((shelf) => (
                            <div key={shelf.id} className="text-center">
                              <div className="text-xs font-medium text-gray-600 mb-1">{shelf.name}</div>
                              <div className="flex flex-col gap-0.5">
                                {shelf.bins.map((binId) => {
                                  const fullBinId = `${selectedWarehouse}-${binId}`;
                                  const bin = binData[fullBinId] || { status: 'empty' };
                                  return (
                                    <div
                                      key={binId}
                                      className={`h-3 rounded ${getBinStatusColor(bin.status)}`}
                                      title={binId}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const slottingSuggestions = getSlottingSuggestions();

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Spatial & Location Management</h2>
            <p className="text-sm text-gray-600 mt-1">Manage warehouse hierarchy: Zones → Aisles → Racks → Shelves → Bins</p>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium"
            >
              {Object.keys(warehouseStructure).map(id => (
                <option key={id} value={id}>{warehouseStructure[id].name}</option>
              ))}
            </select>
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[
                { id: 'hierarchy', label: 'Hierarchy' },
                { id: 'visual', label: 'Visual Map' },
                { id: 'slotting', label: 'Slotting' }
              ].map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => setViewMode(mode.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === mode.id
                      ? 'bg-white text-orange-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* View Content */}
      {viewMode === 'hierarchy' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Warehouse Hierarchy</h3>
            <button className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 text-sm">
              <Printer className="w-4 h-4" />
              Print Bin Labels
            </button>
          </div>
          {renderHierarchy()}
        </motion.div>
      )}

      {viewMode === 'visual' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {renderVisualMap()}
        </motion.div>
      )}

      {viewMode === 'slotting' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Slotting Optimization</h3>
                <p className="text-sm text-gray-600 mt-1">AI-powered suggestions for optimal item placement based on velocity and characteristics</p>
              </div>
              <button
                onClick={handleRunOptimization}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 text-sm"
              >
                <TrendingUp className="w-4 h-4" />
                Run Optimization
              </button>
            </div>
            <div className="space-y-3">
              {slottingSuggestions.map((suggestion, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-gray-900">{suggestion.item}</h4>
                        <span className={`px-2 py-1 text-xs rounded ${
                          suggestion.priority === 'high' ? 'bg-red-100 text-red-700' :
                          suggestion.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {suggestion.priority} priority
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        {suggestion.currentLocation && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Current:</span>
                            <span className="text-gray-900">{suggestion.currentLocation}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Suggested:</span>
                          <span className="text-orange-700 font-semibold">{suggestion.suggestedLocation}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <AlertTriangle className="w-4 h-4 text-orange-600" />
                          <span>{suggestion.reason}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          alert(`Applying slotting suggestion: Move "${suggestion.item}" to ${suggestion.suggestedLocation}`);
                          // In production, this would update the item location in the database
                        }}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                      >
                        Apply
                      </button>
                      <button
                        onClick={() => {
                          // Remove suggestion from list (in production, would update state)
                          alert(`Dismissed suggestion for "${suggestion.item}"`);
                        }}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Velocity Analysis */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Item Velocity Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { label: 'High Velocity', count: 45, color: 'green', description: 'Items near shipping area' },
                { label: 'Medium Velocity', count: 120, color: 'yellow', description: 'Items in mid-range storage' },
                { label: 'Low Velocity', count: 89, color: 'gray', description: 'Items in deep storage' }
              ].map((category) => (
                <div key={category.label} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium px-2 py-1 rounded ${getVelocityColor(category.color === 'green' ? 'high' : category.color === 'yellow' ? 'medium' : 'low')}`}>
                      {category.label}
                    </span>
                    <span className="text-2xl font-bold text-gray-900">{category.count}</span>
                  </div>
                  <p className="text-xs text-gray-600">{category.description}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SpatialLocationManagement;
